import React, { useState } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, uuid, logAudit } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_REQUIREMENT_TYPES } from '../../core/constants';
import { mergeSeedDocTemplates } from '../../core/seed-doc-templates';
import { Icon } from '../../components/Icon';


function BundleSettings() {
  const { bundles, setBundles, docTpls, org, showToast, setView } = useApp();
  const freeResources = Array.isArray(org?.freeResources) ? org.freeResources : [];
  const catalogs = Array.isArray(org?.marketplaceCatalogs) ? org.marketplaceCatalogs : [];
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(null);
  const [saving, setSaving]     = useState(false);

  const gstRate = Number(org?.gstRate) || 0;
  const templates = mergeSeedDocTemplates(docTpls || {});

  const startEdit = (b) => { setEditing(b.id); setForm(JSON.parse(JSON.stringify(b))); };
  const duplicateBundle = (b) => {
    const copy = JSON.parse(JSON.stringify(b));
    copy.id = uuid();
    copy.name = `${b.name} (Copy)`;
    setBundles(bs => [...bs, copy]);
    startEdit(copy);
    showToast("Bundle duplicated — edit and save.", "success");
  };
  const save = async () => {
    if (!form?.name?.trim()) { showToast("Bundle name required", "error"); return; }
    setSaving(true);
    const payload = {
      id: form.id,
      name: form.name,
      description: form.description || "",
      lineItems: form.lineItems || [],
      complianceTasks: form.complianceTasks || [],
      isRecurring: !!form.isRecurring,
      recurringFreq: form.recurringFreq || null,
      installments: form.installments || null,
      freeResourceIds: form.freeResourceIds || [],
      marketplaceCatalogId: form.marketplaceCatalogId || null,
    };
    try {
      // Upsert via dedicated REST — check existence first so new drafts insert cleanly
      const existing = await sb.getBundle(form.id).catch(() => null);
      const saved = existing
        ? await sb.updateBundle(form.id, payload)
        : await sb.createBundle({ ...payload, id: form.id });
      const next = saved && saved.id ? saved : { ...form, ...payload };
      setBundles(bs => {
        const idx = bs.findIndex(b => b.id === form.id);
        if (idx >= 0) return bs.map(b => b.id === form.id ? next : b);
        return [...bs, next];
      });
      setEditing(null);
      showToast("Bundle saved!", "success");
    } catch (e) {
      showToast(e.message || "Failed to save bundle", "error");
    } finally {
      setSaving(false);
    }
  };
  const addItem = () => setForm(f => ({ ...f, lineItems: [...f.lineItems, { id: uuid(), name: "", type: "service", price: 0, gst: true, unit: "fixed", sac: org?.sac || "", tasks: [] }] }));
  const removeItem = (id) => setForm(f => ({ ...f, lineItems: f.lineItems.filter(l => l.id !== id) }));
  const setItem = (id, k, v) => setForm(f => ({ ...f, lineItems: f.lineItems.map(l => l.id === id ? { ...l, [k]: v } : l) }));
  // Recurring compliance tasks (each with a due-date rule) — used by the Recurring Tasks module
  const addCompTask    = () => setForm(f => ({ ...f, complianceTasks: [...(f.complianceTasks||[]), { key: uuid().slice(0,8), name:"", category:"", requirementType:"none", docTemplateId:"", due:{ kind:"annual", month:3, day:31 } }] }));
  const setCompTask    = (i, patch) => setForm(f => ({ ...f, complianceTasks: (f.complianceTasks||[]).map((t,idx)=> idx===i ? { ...t, ...patch } : t) }));
  const setCompDue     = (i, patch) => setForm(f => ({ ...f, complianceTasks: (f.complianceTasks||[]).map((t,idx)=> idx===i ? { ...t, due:{ ...t.due, ...patch } } : t) }));
  const removeCompTask = (i) => setForm(f => ({ ...f, complianceTasks: (f.complianceTasks||[]).filter((_,idx)=>idx!==i) }));
  const MONTHS = ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  if (editing && form) return (
    <div style={{ maxWidth: 800 }}>
      <button className="btn" style={{ marginBottom: 16 }} onClick={() => setEditing(null)}>← Back</button>
      <div className="card">
        <div className="card-head"><div className="card-title">Edit Bundle: {form.name}</div></div>
        <div className="card-body">
          <div className="form-grid-2" style={{ marginBottom: 20 }}>
            <div className="f-group"><label className="f-label">Bundle Name</label><input className="f-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="f-group" style={{ gridColumn: "1/-1" }}><label className="f-label">Description</label><input className="f-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
            <div className="f-group">
              <label className="f-label">Recurring service?</label>
              <select className="f-select" value={form.isRecurring ? "yes" : "no"} onChange={e => setForm(f => ({ ...f, isRecurring: e.target.value === "yes" }))}>
                <option value="no">No — one-time service</option>
                <option value="yes">Yes — recurring / annual</option>
              </select>
            </div>
            {form.isRecurring && <div className="f-group">
              <label className="f-label">Frequency</label>
              <select className="f-select" value={form.recurringFreq || "yearly"} onChange={e => setForm(f => ({ ...f, recurringFreq: e.target.value }))}>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>}
            {form.isRecurring && <div className="f-group">
              <label className="f-label">Split billing into (instalments)</label>
              <input className="f-input" type="number" min="1" value={form.installments || 1} onChange={e => setForm(f => ({ ...f, installments: Number(e.target.value) }))} placeholder="e.g. 3" />
              <div className="f-hint">e.g. annual compliance billed in 3 instalments</div>
            </div>}
            <div className="f-group" style={{ gridColumn: "1/-1" }}>
              <label className="f-label">Marketplace catalog</label>
              <select className="f-select" value={form.marketplaceCatalogId || ""} onChange={e => setForm(f => ({ ...f, marketplaceCatalogId: e.target.value || null }))}>
                <option value="">None — use default catalog</option>
                {catalogs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {freeResources.length > 0 && (
              <div className="f-group" style={{ gridColumn: "1/-1" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <label className="f-label" style={{ margin: 0 }}>Included free resources</label>
                  <button type="button" className="btn btn-sm btn-primary" onClick={() => setView("free-gifts")}>+ Upload free resource</button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {freeResources.map(g => (
                    <label key={g.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                      <input type="checkbox" checked={(form.freeResourceIds || []).includes(g.id)} onChange={() => setForm(f => ({
                        ...f,
                        freeResourceIds: (f.freeResourceIds || []).includes(g.id)
                          ? (f.freeResourceIds || []).filter(x => x !== g.id)
                          : [...(f.freeResourceIds || []), g.id],
                      }))} />
                      {g.name}
                    </label>
                  ))}
                </div>
                <div className="f-hint">Clients unlock these downloads when they purchase this bundle.</div>
              </div>
            )}
          </div>

          {form.isRecurring && (
            <div style={{ background:"#FBFAF7", border:"1px solid var(--border)", borderRadius:10, padding:14, marginBottom:20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Compliance Tasks (auto-scheduled)</div>
              <div className="f-hint" style={{ marginBottom: 10 }}>
                Each task here is created with a computed due date when you assign this package to clients in <strong>Recurring Tasks</strong>. Billing stays as one line item.
              </div>
              {(form.complianceTasks||[]).map((t, i) => (
                <div key={t.key||i} style={{ background:"#fff", border:"1px solid var(--border)", borderRadius:8, padding:12, marginBottom:8 }}>
                  <div style={{ display:"flex", gap:8, marginBottom:8 }}>
                    <input className="f-input" style={{ flex:1 }} placeholder="Task name (e.g. GSTR-3B)" value={t.name} onChange={e=>setCompTask(i,{name:e.target.value})} />
                    <input className="f-input" style={{ width:130 }} placeholder="Category" value={t.category} onChange={e=>setCompTask(i,{category:e.target.value})} />
                    <button className="btn btn-sm btn-red" onClick={()=>removeCompTask(i)}><Icon name="close" size={13} /></button>
                  </div>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                    <select className="f-select" style={{ width:170 }} value={t.due?.kind||"annual"} onChange={e=>{
                      const k=e.target.value;
                      const preset = k==="annual"?{kind:k,month:3,day:31}:k==="monthly"?{kind:k,day:20,offsetMonths:1}:k==="quarterly"?{kind:k,months:[7,10,1,5],day:31}:{kind:k,anchor:"incorporation",days:30};
                      setCompTask(i,{due:preset});
                    }}>
                      <option value="annual">Annual — fixed date</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly — fixed dates</option>
                      <option value="offset">One-time — X days after event</option>
                    </select>
                    {t.due?.kind==="annual" && <>
                      <label style={{ fontSize:12, color:"var(--muted)" }}>Month</label>
                      <select className="f-select" style={{ width:90 }} value={t.due.month||1} onChange={e=>setCompDue(i,{month:Number(e.target.value)})}>
                        {MONTHS.slice(1).map((m,idx)=><option key={m} value={idx+1}>{m}</option>)}
                      </select>
                      <label style={{ fontSize:12, color:"var(--muted)" }}>Day</label>
                      <input className="f-input" style={{ width:70 }} type="number" min="1" max="31" value={t.due.day||1} onChange={e=>setCompDue(i,{day:Number(e.target.value)})} />
                    </>}
                    {t.due?.kind==="monthly" && <>
                      <label style={{ fontSize:12, color:"var(--muted)" }}>Day</label>
                      <input className="f-input" style={{ width:70 }} type="number" min="1" max="31" value={t.due.day||1} onChange={e=>setCompDue(i,{day:Number(e.target.value)})} />
                      <select className="f-select" style={{ width:160 }} value={t.due.offsetMonths||0} onChange={e=>setCompDue(i,{offsetMonths:Number(e.target.value)})}>
                        <option value={0}>of the same month</option>
                        <option value={1}>of the next month</option>
                      </select>
                    </>}
                    {t.due?.kind==="quarterly" && <>
                      <label style={{ fontSize:12, color:"var(--muted)" }}>Months (1-12, comma)</label>
                      <input className="f-input" style={{ width:130 }} value={(t.due.months||[]).join(",")} onChange={e=>setCompDue(i,{months:e.target.value.split(",").map(x=>Number(x.trim())).filter(Boolean)})} placeholder="7,10,1,5" />
                      <label style={{ fontSize:12, color:"var(--muted)" }}>Day</label>
                      <input className="f-input" style={{ width:70 }} type="number" min="1" max="31" value={t.due.day||1} onChange={e=>setCompDue(i,{day:Number(e.target.value)})} />
                    </>}
                    {t.due?.kind==="offset" && <>
                      <select className="f-select" style={{ width:180 }} value={t.due.anchor||"incorporation"} onChange={e=>setCompDue(i,{anchor:e.target.value})}>
                        <option value="incorporation">days after incorporation</option>
                        <option value="package_start">days after package start</option>
                      </select>
                      <input className="f-input" style={{ width:80 }} type="number" min="1" value={t.due.days||1} onChange={e=>setCompDue(i,{days:Number(e.target.value)})} />
                      <span style={{ fontSize:12, color:"var(--muted)" }}>days</span>
                    </>}
                  </div>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:8, alignItems:"center" }}>
                    <select className="f-select" style={{ width:170 }} value={t.requirementType||"none"} onChange={e=>{
                      const v = e.target.value;
                      setCompTask(i, { requirementType: v, docTemplateId: v === "none" ? "" : (t.docTemplateId || "") });
                    }}>
                      {TASK_REQUIREMENT_TYPES.map(rt=><option key={rt.id} value={rt.id}>{rt.label}</option>)}
                    </select>
                    {(t.requirementType === "docs" || t.requirementType === "form_docs") && (
                      <select className="f-select" style={{ flex:1, minWidth:180 }} value={t.docTemplateId||""} onChange={e=>setCompTask(i,{ docTemplateId: e.target.value })}>
                        <option value="">Select template…</option>
                        {Object.values(templates).map(dt=><option key={dt.id} value={dt.id}>{dt.name}</option>)}
                      </select>
                    )}
                  </div>
                </div>
              ))}
              <button className="btn btn-ghost btn-sm" onClick={addCompTask}>+ Add Compliance Task</button>
            </div>
          )}

          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Line Items</div>
          {form.lineItems.map((li, idx) => (
            <div key={li.id} style={{ background: "#FAFAF8", border: "1px solid var(--border)", borderRadius: 10, padding: 14, marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>Item {idx + 1}</span>
                <button className="btn btn-sm btn-red" onClick={() => removeItem(li.id)}>Remove</button>
              </div>
              <div className="form-grid-2">
                <div className="f-group"><label className="f-label">Item Name</label><input className="f-input" value={li.name} onChange={e => setItem(li.id, "name", e.target.value)} /></div>
                <div className="f-group">
                  <label className="f-label">Type</label>
                  <select className="f-select" value={li.type} onChange={e => setItem(li.id, "type", e.target.value)}>
                    <option value="govt">Government Fee (no task)</option>
                    <option value="dsc">DSC (task per qty)</option>
                    <option value="service">Professional Service</option>
                  </select>
                </div>
                <div className="f-group">
                  <label className="f-label">SAC Code</label>
                  {/* HARDCODED placeholders removed — use org default SAC from Organisation Settings
                  <input ... placeholder={li.type==="govt"?"999799":li.type==="dsc"?"998315":"998211"} />
                  <div className="f-hint">Govt: 999799 · DSC: 998315 · Professional: 998211</div>
                  */}
                  <input className="f-input" value={li.sac||""} onChange={e=>setItem(li.id,"sac",e.target.value)} placeholder={org?.sac || "SAC code"} />
                  {org?.sac && <div className="f-hint">Org default SAC: {org.sac}</div>}
                </div>
                <div className="f-group"><label className="f-label">Unit Price (₹)</label><input className="f-input" type="number" value={li.price || ""} onChange={e => setItem(li.id, "price", Number(e.target.value))} placeholder="0 = enter on invoice" /></div>
                <div className="f-group">
                  <label className="f-label">Pricing</label>
                  <select className="f-select" value={li.unit} onChange={e => setItem(li.id, "unit", e.target.value)}>
                    <option value="fixed">Fixed</option>
                    <option value="per_unit">Per Unit / Qty</option>
                  </select>
                </div>
                <div className="f-group">
                  <label className="f-label">GST Applicable</label>
                  {/* HARDCODED "+18% GST" removed — use org.gstRate */}
                  <select className="f-select" value={li.gst ? "yes" : "no"} onChange={e => setItem(li.id, "gst", e.target.value === "yes")}>
                    <option value="yes">Yes{gstRate ? ` (+${gstRate}% GST)` : " (GST)"}</option>
                    <option value="no">No (Govt fee)</option>
                  </select>
                </div>
              </div>
              {li.type !== "govt" && (
                <div style={{ marginTop: 10 }}>
                  <label className="f-label">Auto-created Tasks</label>
                  <div className="f-hint" style={{ marginBottom: 6 }}>
                    Each task below is auto-created when this invoice line item is added.
                    {li.type === "dsc" && " DSC tasks repeat once per director (based on qty)."}
                  </div>
                  {(li.tasks || []).map((task, ti) => {
                    const tpl = task.docTemplateId ? templates[task.docTemplateId] : null;
                    return (
                    <div key={ti} style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 8, padding: 12, marginBottom: 8 }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                        <input
                          className="f-input"
                          style={{ flex: 1 }}
                          placeholder={li.type === "dsc" ? "DSC Creation for Director {n}" : `Task ${ti + 1} name`}
                          value={task.name}
                          onChange={e => {
                            const t = [...(li.tasks || [])];
                            t[ti] = { ...t[ti], name: e.target.value };
                            setItem(li.id, "tasks", t);
                          }}
                        />
                        <button className="btn btn-sm btn-red" onClick={() => setItem(li.id, "tasks", (li.tasks||[]).filter((_,i)=>i!==ti))}><Icon name="close" size={13} /></button>
                      </div>
                      <div className="form-grid-2">
                        <div className="f-group">
                          <label className="f-label" style={{ fontSize: 9 }}>Collection Level</label>
                          <select className="f-select" style={{ fontSize: 12, padding: "5px 8px" }}
                            value={task.autoFromQty ? "director" : "common"}
                            onChange={e => {
                              const t = [...(li.tasks||[])];
                              t[ti] = { ...t[ti], autoFromQty: e.target.value === "director" };
                              setItem(li.id, "tasks", t);
                            }}>
                            <option value="common">Common — once per company</option>
                            <option value="director">Director-level — once per director</option>
                          </select>
                        </div>
                        <div className="f-group">
                          <label className="f-label" style={{ fontSize: 9 }}>Document Template</label>
                          {/* HARDCODED DEFAULT_DOC_TEMPLATES removed — options from GET /document-templates via docTpls */}
                          <select className="f-select" style={{ fontSize: 12, padding: "5px 8px" }}
                            value={task.docTemplateId || ""}
                            onChange={e => {
                              const t = [...(li.tasks||[])];
                              const id = e.target.value;
                              // const tpl = DEFAULT_DOC_TEMPLATES[id];
                              t[ti] = {
                                ...t[ti],
                                docTemplateId: id,
                                requirementType: id ? "form_docs" : "none",
                              };
                              setItem(li.id, "tasks", t);
                            }}>
                            <option value="">No collection needed</option>
                            {Object.values(templates).map(t => (
                              <option key={t.id} value={t.id}>{t.name} ({t.collectionType})</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {tpl && (
                        <div style={{ marginTop: 8, padding: "8px 12px", background: "#F0F9FF", borderRadius: 6, fontSize: 11, color: "#0369A1" }}>
                          <Icon name="clipboard" size={12} /> <strong>{tpl.name}</strong>:
                          {" "}{(tpl.infoFields||[]).length} info fields
                          {" · "}{(tpl.docSlots||[]).length} document slots
                          {" · "}<strong>{tpl.collectionType === "director" ? "Per Director" : "Per Company"}</strong>
                          <br/><span style={{ color: "#6B7280" }}>Edit fields in Settings → Document Templates</span>
                        </div>
                      )}
                    </div>
                    );
                  })}
                  <button className="btn btn-ghost btn-sm" onClick={() => setItem(li.id, "tasks", [...(li.tasks||[]), { name: "", autoFromQty: li.type==="dsc", requirementType: "none", docTemplateId: "" }])}>
                    + Add Task
                  </button>
                </div>
              )}
            </div>
          ))}
          <button className="btn btn-ghost" onClick={addItem}>+ Add Line Item</button>
        </div>
        <div style={{ padding: "0 20px 20px" }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save Bundle"}</button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
        <button className="btn btn-primary" onClick={() => {
          // Blank draft — name is editable; not a seeded/hardcoded catalog entry
          const newBundle = { id: uuid(), name: "New Bundle", description: "", lineItems: [], complianceTasks: [], isRecurring: false, totalApprox: null };
          setBundles(bs => [...bs, newBundle]);
          startEdit(newBundle);
        }}>+ New Bundle</button>
      </div>
      <div className="grid2">
        {bundles.map(b => (
          <div key={b.id} className="card">
            <div className="card-head">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Icon name="package" size={22} />
                <div>
                  <div className="card-title">{b.name}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>{b.description}</div>
                </div>
              </div>
              <button className="btn btn-sm" onClick={() => startEdit(b)}>Edit</button>
              <button className="btn btn-sm" onClick={() => duplicateBundle(b)}>Duplicate</button>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {(b.lineItems || []).map((li) => (
                <div key={li.id} className="row-item">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{li.name}</div>
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>{li.type} · {li.gst ? "+GST" : "no GST"} · {li.unit}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: li.price ? "var(--ink)" : "var(--muted)" }}>
                    {li.price ? INR(li.price) : "Variable"}
                  </div>
                </div>
              ))}
              {b.totalApprox && <div style={{ padding: "12px 18px", fontSize: 13, fontWeight: 700, borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between" }}><span>Approx Total</span><span style={{ color: "var(--blue)" }}>{INR(b.totalApprox)}</span></div>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export { BundleSettings };
